import { useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { HiOutlinePlus, HiOutlineTrash, HiOutlineArrowUp, HiOutlineArrowDown, HiOutlineCog } from "react-icons/hi";
import { createSurvey, updateSurvey } from "../../redux/actions/surveyActions";
import { Snackbar } from "../shared/Snackbar";
import Button from "../shared/Button";
import TextInput from "../shared/TextInput";
import Card from "../shared/Card";
import Modal from "../shared/Modal";
import Badge from "../shared/Badge";
import { cn } from "../../utils/cn";

const QUESTION_TYPES = [
    { value: "single_choice", label: "Single Choice" },
    { value: "multiple_choice", label: "Multiple Choice" },
    { value: "text_short", label: "Short Text" },
    { value: "text_long", label: "Long Text" },
    { value: "rating_scale", label: "Rating Scale" },
    { value: "nps", label: "NPS (0–10)" },
    { value: "yes_no", label: "Yes/No" },
    { value: "dropdown", label: "Dropdown" },
    { value: "email", label: "Email" },
    { value: "number", label: "Number" },
    { value: "date", label: "Date" },
];

const OPERATORS = [
    { value: "equals", label: "is equal to" },
    { value: "notEquals", label: "is not equal to" },
    { value: "contains", label: "contains" },
];

const toLocalDateInput = (date) => {
    if (!date) return "";
    const d = new Date(date);
    if (isNaN(d.getTime())) return "";
    const offsetMs = d.getTime() - d.getTimezoneOffset() * 60000;
    return new Date(offsetMs).toISOString().slice(0, 16);
};

const SurveyBuilderModal = ({ survey, closeModal }) => {
    const dispatch = useDispatch();
    const currentOrganisation = useSelector((state) => state.organisation?.currentOrganisation);
    const [snackbar, setSnackbar] = useState({ show: false, message: "", type: "success" });

    const [surveyData, setSurveyData] = useState({
        title: survey?.title || "",
        description: survey?.description || "",
        status: survey?.status || "draft",
        questions: survey?.questions || [],
        startDate: survey?.startDate || null,
        endDate: survey?.endDate || null,
        settings: survey?.settings || {
            allowAnonymous: true,
            showProgressBar: true,
            allowMultipleResponses: false,
            shuffleQuestions: false,
            requireLogin: false,
            showResultsToRespondents: false,
            maxResponses: null,
        },
        translations: survey?.translations || {},
        styling: survey?.styling || {
            primaryColor: "#181d26",
            backgroundColor: "#FFFFFF",
        },
    });

    const [activeTab, setActiveTab] = useState("questions");
    const [translationLang, setTranslationLang] = useState("");

    const showSnackbar = (message, type = "success") => {
        setSnackbar({ show: true, message, type });
        setTimeout(() => setSnackbar({ show: false, message: "", type: "success" }), 3000);
    };

    const updateSetting = (key, value) =>
        setSurveyData({
            ...surveyData,
            settings: { ...surveyData.settings, [key]: value },
        });

    const addQuestion = () => {
        const newQuestion = {
            questionText: "",
            questionType: "single_choice",
            required: false,
            options: [
                { text: "Option 1", value: "option_1" },
                { text: "Option 2", value: "option_2" },
            ],
            ratingConfig: { min: 1, max: 5, minLabel: "", maxLabel: "" },
            placeholder: "",
            order: surveyData.questions.length,
            showIf: null,
        };
        setSurveyData({ ...surveyData, questions: [...surveyData.questions, newQuestion] });
    };

    const updateQuestion = (index, field, value) => {
        const updatedQuestions = [...surveyData.questions];
        updatedQuestions[index] = { ...updatedQuestions[index], [field]: value };
        setSurveyData({ ...surveyData, questions: updatedQuestions });
    };

    const deleteQuestion = (index) => {
        const updatedQuestions = surveyData.questions.filter((_, i) => i !== index);
        updatedQuestions.forEach((q, i) => (q.order = i));
        setSurveyData({ ...surveyData, questions: updatedQuestions });
    };

    const moveQuestion = (index, direction) => {
        const newIndex = direction === "up" ? index - 1 : index + 1;
        if (newIndex < 0 || newIndex >= surveyData.questions.length) return;
        const updatedQuestions = [...surveyData.questions];
        [updatedQuestions[index], updatedQuestions[newIndex]] = [
            updatedQuestions[newIndex],
            updatedQuestions[index],
        ];
        updatedQuestions.forEach((q, i) => (q.order = i));
        setSurveyData({ ...surveyData, questions: updatedQuestions });
    };

    const addOption = (questionIndex) => {
        const updatedQuestions = [...surveyData.questions];
        const optionNumber = updatedQuestions[questionIndex].options.length + 1;
        updatedQuestions[questionIndex].options.push({
            text: `Option ${optionNumber}`,
            value: `option_${optionNumber}`,
        });
        setSurveyData({ ...surveyData, questions: updatedQuestions });
    };

    const updateOption = (questionIndex, optionIndex, field, value) => {
        const updatedQuestions = [...surveyData.questions];
        updatedQuestions[questionIndex].options[optionIndex][field] = value;
        if (field === "text") {
            updatedQuestions[questionIndex].options[optionIndex].value = value
                .toLowerCase()
                .replace(/\s+/g, "_");
        }
        setSurveyData({ ...surveyData, questions: updatedQuestions });
    };

    const deleteOption = (questionIndex, optionIndex) => {
        const updatedQuestions = [...surveyData.questions];
        updatedQuestions[questionIndex].options = updatedQuestions[questionIndex].options.filter(
            (_, i) => i !== optionIndex
        );
        setSurveyData({ ...surveyData, questions: updatedQuestions });
    };

    const updateShowIf = (questionIndex, patch) => {
        const updatedQuestions = [...surveyData.questions];
        const current = updatedQuestions[questionIndex].showIf || {};
        const next = { ...current, ...patch };
        if (!next.questionId) {
            updatedQuestions[questionIndex].showIf = null;
        } else {
            updatedQuestions[questionIndex].showIf = {
                questionId: next.questionId,
                operator: next.operator || "equals",
                value: next.value,
            };
        }
        setSurveyData({ ...surveyData, questions: updatedQuestions });
    };

    const handleSave = async () => {
        if (!surveyData.title.trim()) return showSnackbar("Please enter a survey title", "error");
        if (surveyData.questions.length === 0) return showSnackbar("Please add at least one question", "error");
        for (let i = 0; i < surveyData.questions.length; i++) {
            const q = surveyData.questions[i];
            if (!q.questionText.trim()) return showSnackbar(`Question ${i + 1} is missing text`, "error");
            if (["single_choice", "multiple_choice", "dropdown"].includes(q.questionType)) {
                if (!q.options || q.options.length < 2) {
                    return showSnackbar(`Question ${i + 1} needs at least 2 options`, "error");
                }
            }
        }
        if (surveyData.startDate && surveyData.endDate && new Date(surveyData.startDate) >= new Date(surveyData.endDate)) {
            return showSnackbar("End date must be after start date", "error");
        }
        if (surveyData.settings.maxResponses != null && surveyData.settings.maxResponses < 1) {
            return showSnackbar("Max responses must be at least 1", "error");
        }

        let result;
        if (survey) {
            result = await dispatch(updateSurvey(survey._id, surveyData));
        } else {
            result = await dispatch(createSurvey(currentOrganisation._id, surveyData));
        }

        if (result.success) {
            showSnackbar(survey ? "Survey updated successfully" : "Survey created successfully");
            setTimeout(() => closeModal(), 1500);
        } else {
            showSnackbar(result.error || "Failed to save survey", "error");
        }
    };

    const requiresOptions = (type) =>
        ["single_choice", "multiple_choice", "dropdown"].includes(type);
    const requiresRatingConfig = (type) => type === "rating_scale";
    const requiresPlaceholder = (type) =>
        ["text_short", "text_long", "email", "number"].includes(type);

    const tabClass = (tab) =>
        cn(
            "px-6 py-3 font-medium transition-colors border-b-2",
            activeTab === tab
                ? "text-ink border-ink"
                : "text-muted border-transparent hover:text-ink"
        );

    const settingItems = [
        {
            key: "allowAnonymous",
            title: "Allow Anonymous Responses",
            description: "Allow users to respond without logging in",
        },
        {
            key: "requireLogin",
            title: "Require Login",
            description: "Only allow logged-in members to respond",
        },
        {
            key: "showProgressBar",
            title: "Show Progress Bar",
            description: "Display progress indicator to respondents",
        },
        {
            key: "allowMultipleResponses",
            title: "Allow Multiple Responses",
            description: "Let users submit multiple responses",
        },
        {
            key: "shuffleQuestions",
            title: "Shuffle Questions",
            description: "Randomize question order for each respondent",
        },
        {
            key: "showResultsToRespondents",
            title: "Show Results After Submission",
            description: "Display aggregated results to respondents after they finish",
        },
    ];

    const renderConditionalLogic = (question, qIndex) => {
        const possiblePrior = surveyData.questions
            .slice(0, qIndex)
            .map((q) => ({ id: String(q._id || q.tempId || `idx-${qIndex}`), text: q.questionText, type: q.questionType, options: q.options }))
            .filter((q) => q.text);
        const showIf = question.showIf || null;
        const referenced = showIf
            ? surveyData.questions.find((q) => String(q._id) === String(showIf.questionId))
            : null;
        return (
            <div className="mt-3 p-3 rounded-md border border-hairline bg-canvas">
                <div className="flex items-center gap-2 mb-2">
                    <HiOutlineCog className="text-ink" />
                    <span className="text-body-md font-medium text-ink">Conditional logic</span>
                    {showIf && <Badge variant="info" size="sm">Active</Badge>}
                </div>
                {qIndex === 0 ? (
                    <p className="text-body-md text-muted">
                        Logic can only depend on earlier questions.
                    </p>
                ) : (
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-2">
                        <select
                            value={showIf?.questionId || ""}
                            onChange={(e) => updateShowIf(qIndex, { questionId: e.target.value })}
                            className="h-11 px-3 rounded-sm border border-hairline bg-canvas text-ink text-body-md"
                        >
                            <option value="">Always show</option>
                            {possiblePrior.map((q) => (
                                <option key={q.id} value={q.id}>
                                    {q.text.slice(0, 40)}
                                </option>
                            ))}
                        </select>
                        {showIf?.questionId && (
                            <>
                                <select
                                    value={showIf.operator || "equals"}
                                    onChange={(e) => updateShowIf(qIndex, { operator: e.target.value })}
                                    className="h-11 px-3 rounded-sm border border-hairline bg-canvas text-ink text-body-md"
                                >
                                    {OPERATORS.map((op) => (
                                        <option key={op.value} value={op.value}>
                                            {op.label}
                                        </option>
                                    ))}
                                </select>
                                {referenced?.questionType === "yes_no" ? (
                                    <select
                                        value={String(showIf.value ?? "")}
                                        onChange={(e) => updateShowIf(qIndex, { value: e.target.value === "true" })}
                                        className="h-11 px-3 rounded-sm border border-hairline bg-canvas text-ink text-body-md"
                                    >
                                        <option value="">—</option>
                                        <option value="true">Yes</option>
                                        <option value="false">No</option>
                                    </select>
                                ) : referenced && requiresOptions(referenced.questionType) ? (
                                    <select
                                        value={showIf.value ?? ""}
                                        onChange={(e) => updateShowIf(qIndex, { value: e.target.value })}
                                        className="h-11 px-3 rounded-sm border border-hairline bg-canvas text-ink text-body-md"
                                    >
                                        <option value="">—</option>
                                        {(referenced.options || []).map((opt) => (
                                            <option key={opt.value} value={opt.value}>
                                                {opt.text}
                                            </option>
                                        ))}
                                    </select>
                                ) : (
                                    <TextInput
                                        type="text"
                                        value={showIf.value ?? ""}
                                        onChange={(e) => updateShowIf(qIndex, { value: e.target.value })}
                                        placeholder="Value"
                                    />
                                )}
                            </>
                        )}
                    </div>
                )}
            </div>
        );
    };

    const footer = (
        <>
            <Button variant="secondary" size="sm" onClick={closeModal}>
                Cancel
            </Button>
            <Button variant="primary" size="sm" onClick={handleSave}>
                {survey ? "Update Survey" : "Create Survey"}
            </Button>
        </>
    );

    const translationLanguages = Object.keys(surveyData.translations || {});
    const activeTranslation = translationLang ? surveyData.translations[translationLang] || {} : null;

    const updateTranslation = (patch) => {
        if (!translationLang) return;
        setSurveyData({
            ...surveyData,
            translations: {
                ...surveyData.translations,
                [translationLang]: { ...activeTranslation, ...patch },
            },
        });
    };

    const updateTranslationQuestion = (questionId, patch) => {
        const next = { ...(activeTranslation?.questions || {}) };
        next[questionId] = { ...(next[questionId] || {}), ...patch };
        updateTranslation({ questions: next });
    };

    return (
        <>
            <Modal
                isOpen={true}
                onClose={closeModal}
                title={survey ? "Edit Survey" : "Create Survey"}
                size="lg"
                bodyPadding={false}
                footer={footer}
            >
                <div className="flex border-b border-hairline">
                    <button onClick={() => setActiveTab("questions")} className={tabClass("questions")}>
                        Questions
                    </button>
                    <button onClick={() => setActiveTab("settings")} className={tabClass("settings")}>
                        Settings
                    </button>
                    <button onClick={() => setActiveTab("translations")} className={tabClass("translations")}>
                        Translations
                    </button>
                </div>

                <div className="p-6">
                    {activeTab === "questions" && (
                        <div className="space-y-6">
                            <TextInput
                                label="Survey Title *"
                                type="text"
                                value={surveyData.title}
                                onChange={(e) => setSurveyData({ ...surveyData, title: e.target.value })}
                                placeholder="Enter survey title"
                            />
                            <TextInput
                                label="Description"
                                multiline
                                rows={3}
                                value={surveyData.description}
                                onChange={(e) =>
                                    setSurveyData({ ...surveyData, description: e.target.value })
                                }
                                placeholder="Enter survey description (optional)"
                            />
                            <div>
                                <div className="flex justify-between items-center mb-4">
                                    <h3 className="text-title-sm text-ink">Questions</h3>
                                    <Button
                                        variant="primary"
                                        size="sm"
                                        onClick={addQuestion}
                                        leadingIcon={<HiOutlinePlus />}
                                    >
                                        Add Question
                                    </Button>
                                </div>

                                {surveyData.questions.length === 0 ? (
                                    <Card variant="surface-soft" className="text-center !p-12 border-2 border-dashed border-hairline">
                                        <p className="text-body-md text-muted">
                                            No questions yet. Click "Add Question" to get started.
                                        </p>
                                    </Card>
                                ) : (
                                    <div className="space-y-3">
                                        {surveyData.questions.map((question, qIndex) => (
                                            <Card key={qIndex} variant="surface-soft" className="!p-4">
                                                <div className="flex justify-between items-start mb-3">
                                                    <div className="flex items-center gap-2">
                                                        <span className="text-body-md font-medium text-muted">
                                                            Question {qIndex + 1}
                                                        </span>
                                                        {question.showIf?.questionId && (
                                                            <Badge variant="info" size="sm">Logic</Badge>
                                                        )}
                                                    </div>
                                                    <div className="flex gap-1">
                                                        <button
                                                            onClick={() => moveQuestion(qIndex, "up")}
                                                            disabled={qIndex === 0}
                                                            className="p-1 rounded-md text-muted hover:text-ink hover:bg-canvas disabled:opacity-30"
                                                        >
                                                            <HiOutlineArrowUp />
                                                        </button>
                                                        <button
                                                            onClick={() => moveQuestion(qIndex, "down")}
                                                            disabled={qIndex === surveyData.questions.length - 1}
                                                            className="p-1 rounded-md text-muted hover:text-ink hover:bg-canvas disabled:opacity-30"
                                                        >
                                                            <HiOutlineArrowDown />
                                                        </button>
                                                        <button
                                                            onClick={() => deleteQuestion(qIndex)}
                                                            className="p-1 rounded-md text-muted hover:text-signature-coral hover:bg-canvas"
                                                        >
                                                            <HiOutlineTrash />
                                                        </button>
                                                    </div>
                                                </div>

                                                <TextInput
                                                    type="text"
                                                    value={question.questionText}
                                                    onChange={(e) =>
                                                        updateQuestion(qIndex, "questionText", e.target.value)
                                                    }
                                                    placeholder="Enter question text"
                                                    containerClassName="mb-3"
                                                />

                                                <div className="grid grid-cols-2 gap-3 mb-3">
                                                    <select
                                                        value={question.questionType}
                                                        onChange={(e) =>
                                                            updateQuestion(qIndex, "questionType", e.target.value)
                                                        }
                                                        className="h-11 px-4 rounded-sm border border-hairline bg-canvas text-ink text-body-md focus:outline-none focus:border-info-border"
                                                    >
                                                        {QUESTION_TYPES.map((type) => (
                                                            <option key={type.value} value={type.value}>
                                                                {type.label}
                                                            </option>
                                                        ))}
                                                    </select>
                                                    <label className="flex items-center gap-2 px-4 h-11 rounded-sm border border-hairline bg-canvas cursor-pointer">
                                                        <input
                                                            type="checkbox"
                                                            checked={question.required}
                                                            onChange={(e) =>
                                                                updateQuestion(qIndex, "required", e.target.checked)
                                                            }
                                                            className="rounded"
                                                        />
                                                        <span className="text-body-md text-ink">Required</span>
                                                    </label>
                                                </div>

                                                {requiresOptions(question.questionType) && (
                                                    <div className="mt-3 space-y-2">
                                                        <div className="flex justify-between items-center">
                                                            <span className="text-body-md font-medium text-muted">
                                                                Options
                                                            </span>
                                                            <Button
                                                                variant="ghost"
                                                                size="sm"
                                                                onClick={() => addOption(qIndex)}
                                                            >
                                                                + Add Option
                                                            </Button>
                                                        </div>
                                                        {question.options.map((option, oIndex) => (
                                                            <div key={oIndex} className="flex gap-2 items-start">
                                                                <div className="flex-1">
                                                                    <TextInput
                                                                        type="text"
                                                                        value={option.text}
                                                                        onChange={(e) =>
                                                                            updateOption(qIndex, oIndex, "text", e.target.value)
                                                                        }
                                                                        placeholder={`Option ${oIndex + 1}`}
                                                                    />
                                                                </div>
                                                                {question.options.length > 2 && (
                                                                    <button
                                                                        onClick={() => deleteOption(qIndex, oIndex)}
                                                                        className="p-2 rounded-md text-muted hover:text-signature-coral hover:bg-canvas h-11"
                                                                    >
                                                                        <HiOutlineTrash />
                                                                    </button>
                                                                )}
                                                            </div>
                                                        ))}
                                                    </div>
                                                )}

                                                {requiresRatingConfig(question.questionType) && (
                                                    <div className="mt-3 grid grid-cols-2 gap-3">
                                                        <TextInput
                                                            label="Min Value"
                                                            type="number"
                                                            value={question.ratingConfig.min}
                                                            onChange={(e) =>
                                                                updateQuestion(qIndex, "ratingConfig", {
                                                                    ...question.ratingConfig,
                                                                    min: parseInt(e.target.value),
                                                                })
                                                            }
                                                        />
                                                        <TextInput
                                                            label="Max Value"
                                                            type="number"
                                                            value={question.ratingConfig.max}
                                                            onChange={(e) =>
                                                                updateQuestion(qIndex, "ratingConfig", {
                                                                    ...question.ratingConfig,
                                                                    max: parseInt(e.target.value),
                                                                })
                                                            }
                                                        />
                                                    </div>
                                                )}

                                                {requiresPlaceholder(question.questionType) && (
                                                    <TextInput
                                                        type="text"
                                                        value={question.placeholder || ""}
                                                        onChange={(e) =>
                                                            updateQuestion(qIndex, "placeholder", e.target.value)
                                                        }
                                                        placeholder="Placeholder text (optional)"
                                                        containerClassName="mt-3"
                                                    />
                                                )}

                                                {renderConditionalLogic(question, qIndex)}
                                            </Card>
                                        ))}
                                    </div>
                                )}
                            </div>
                        </div>
                    )}

                    {activeTab === "settings" && (
                        <div className="space-y-6">
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                                <TextInput
                                    label="Start date (optional)"
                                    type="datetime-local"
                                    value={toLocalDateInput(surveyData.startDate)}
                                    onChange={(e) =>
                                        setSurveyData({
                                            ...surveyData,
                                            startDate: e.target.value ? new Date(e.target.value) : null,
                                        })
                                    }
                                />
                                <TextInput
                                    label="End date (optional)"
                                    type="datetime-local"
                                    value={toLocalDateInput(surveyData.endDate)}
                                    onChange={(e) =>
                                        setSurveyData({
                                            ...surveyData,
                                            endDate: e.target.value ? new Date(e.target.value) : null,
                                        })
                                    }
                                />
                            </div>

                            <TextInput
                                label="Maximum total responses (optional)"
                                type="number"
                                min="1"
                                value={surveyData.settings.maxResponses ?? ""}
                                onChange={(e) =>
                                    updateSetting(
                                        "maxResponses",
                                        e.target.value ? parseInt(e.target.value, 10) : null
                                    )
                                }
                                placeholder="Leave empty for unlimited"
                            />

                            <div className="space-y-3">
                                <h3 className="text-title-sm text-ink">Survey behavior</h3>
                                {settingItems.map((setting) => (
                                    <label
                                        key={setting.key}
                                        className="flex items-center gap-3 p-4 rounded-md border border-hairline cursor-pointer hover:bg-surface-soft transition-colors"
                                    >
                                        <input
                                            type="checkbox"
                                            checked={!!surveyData.settings[setting.key]}
                                            onChange={(e) => updateSetting(setting.key, e.target.checked)}
                                            className="rounded"
                                        />
                                        <div>
                                            <div className="font-medium text-ink">{setting.title}</div>
                                            <div className="text-body-md text-muted">{setting.description}</div>
                                        </div>
                                    </label>
                                ))}
                            </div>
                        </div>
                    )}

                    {activeTab === "translations" && (
                        <div className="space-y-4">
                            <div className="flex flex-wrap items-center gap-3">
                                <span className="text-body-md text-ink">Language:</span>
                                <select
                                    value={translationLang}
                                    onChange={(e) => setTranslationLang(e.target.value)}
                                    className="h-11 px-3 rounded-sm border border-hairline bg-canvas text-ink text-body-md"
                                >
                                    <option value="">Select…</option>
                                    {translationLanguages.map((lng) => (
                                        <option key={lng} value={lng}>
                                            {lng}
                                        </option>
                                    ))}
                                </select>
                                <Button
                                    variant="secondary"
                                    size="sm"
                                    onClick={() => {
                                        const lng = (window.prompt("Language code (e.g. de, fr, es):") || "")
                                            .trim()
                                            .toLowerCase();
                                        if (!lng || !/^[a-z]{2}(-[a-z]{2})?$/i.test(lng)) {
                                            return showSnackbar("Enter a 2-letter language code", "error");
                                        }
                                        setSurveyData({
                                            ...surveyData,
                                            translations: {
                                                ...surveyData.translations,
                                                [lng]: surveyData.translations?.[lng] || { questions: {} },
                                            },
                                        });
                                        setTranslationLang(lng);
                                    }}
                                >
                                    + Add language
                                </Button>
                                {translationLang && (
                                    <Button
                                        variant="ghost"
                                        size="sm"
                                        onClick={() => {
                                            const next = { ...surveyData.translations };
                                            delete next[translationLang];
                                            setSurveyData({ ...surveyData, translations: next });
                                            setTranslationLang("");
                                        }}
                                    >
                                        Remove “{translationLang}”
                                    </Button>
                                )}
                            </div>

                            {translationLang && (
                                <div className="space-y-4">
                                    <TextInput
                                        label={`Title (${translationLang})`}
                                        type="text"
                                        value={activeTranslation?.title || ""}
                                        onChange={(e) => updateTranslation({ title: e.target.value })}
                                        placeholder={surveyData.title}
                                    />
                                    <TextInput
                                        label={`Description (${translationLang})`}
                                        multiline
                                        rows={2}
                                        value={activeTranslation?.description || ""}
                                        onChange={(e) => updateTranslation({ description: e.target.value })}
                                        placeholder={surveyData.description}
                                    />

                                    <div className="space-y-3">
                                        {surveyData.questions.map((q, qi) => {
                                            const qid = String(q._id || qi);
                                            const tq = (activeTranslation?.questions || {})[qid] || {};
                                            return (
                                                <Card key={qid} variant="surface-soft" className="!p-4">
                                                    <div className="text-body-md text-muted mb-2">
                                                        Question {qi + 1} ({q.questionType})
                                                    </div>
                                                    <TextInput
                                                        type="text"
                                                        value={tq.questionText || ""}
                                                        onChange={(e) =>
                                                            updateTranslationQuestion(qid, {
                                                                questionText: e.target.value,
                                                            })
                                                        }
                                                        placeholder={q.questionText}
                                                    />
                                                    {requiresOptions(q.questionType) && (
                                                        <div className="mt-3 space-y-2">
                                                            {q.options.map((opt, oi) => {
                                                                const tOpt = (tq.options || []).find(
                                                                    (o) => o.value === opt.value
                                                                ) || { value: opt.value };
                                                                return (
                                                                    <TextInput
                                                                        key={oi}
                                                                        type="text"
                                                                        value={tOpt.text || ""}
                                                                        onChange={(e) => {
                                                                            const next = (tq.options || []).filter(
                                                                                (o) => o.value !== opt.value
                                                                            );
                                                                            next.push({ value: opt.value, text: e.target.value });
                                                                            updateTranslationQuestion(qid, { options: next });
                                                                        }}
                                                                        placeholder={opt.text}
                                                                    />
                                                                );
                                                            })}
                                                        </div>
                                                    )}
                                                </Card>
                                            );
                                        })}
                                    </div>
                                </div>
                            )}

                            {!translationLang && (
                                <p className="text-body-md text-muted">
                                    Add a language to translate the survey title, description, questions, and choice
                                    options. Respondents see the translated version when they visit{" "}
                                    <code>/survey/&lt;id&gt;?lang=&lt;code&gt;</code>.
                                </p>
                            )}
                        </div>
                    )}
                </div>
            </Modal>

            {snackbar.show && (
                <Snackbar
                    text={snackbar.message}
                    successIcon={snackbar.type === "success"}
                    onClose={() => setSnackbar({ show: false, message: "", type: "success" })}
                />
            )}
        </>
    );
};

export default SurveyBuilderModal;
